import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store';
import { api } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { fr, es, pt, de, it, ja, ko, zhCN } from 'date-fns/locale';
import { MessageCircle, User, Bot, AlertCircle, Search, Filter } from 'lucide-react';

export default function ConversationList() {
  const { t, i18n } = useTranslation();
  const {
    conversations,
    setConversations,
    selectedConversation,
    setSelectedConversation,
    statusFilter,
    handlingModeFilter,
    searchQuery,
    setStatusFilter,
    setHandlingModeFilter,
    setSearchQuery,
    setMobileConversationView,
  } = useStore();

  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Get date-fns locale based on current language
  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'pt': return pt;
      case 'de': return de;
      case 'it': return it;
      case 'ja': return ja;
      case 'ko': return ko;
      case 'zh': return zhCN;
      default: return undefined; // English is default
    }
  };

  useEffect(() => {
    loadConversations();
  }, [statusFilter, handlingModeFilter, searchQuery]);

  const loadConversations = async () => {
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (handlingModeFilter) params.handling_mode = handlingModeFilter;
      if (searchQuery) params.search = searchQuery;

      const data = await api.getConversations(params);
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get display name - use actual name, or infer from email
  const getDisplayName = (conversation: any) => {
    const name = conversation.contact?.name || (conversation as any).contact_name;
    const email = conversation.contact?.email || (conversation as any).contact_email;

    // If they have a real name (not "Anonymous"), use it
    if (name && name.toLowerCase() !== 'anonymous') {
      return name;
    }
    // If they have an email, infer name from it
    if (email) {
      const localPart = email.split('@')[0];
      // Replace dots, underscores, hyphens with spaces and title case
      return localPart
        .replace(/[._-]/g, ' ')
        .replace(/\b\w/g, (c: string) => c.toUpperCase());
    }
    // Fallback
    return t('conversations.guest');
  };

  const getHandlingModeIcon = (mode: string) => {
    switch (mode) {
      case 'ai':
        return <Bot className="w-4 h-4 text-green-600" />;
      case 'ai_suggested':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'human':
        return <User className="w-4 h-4 text-red-600" />;
      case 'hybrid':
        return <MessageCircle className="w-4 h-4 text-blue-600" />;
      default:
        return <MessageCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getHandlingModeBadge = (mode: string) => {
    switch (mode) {
      case 'ai':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">🤖 {t('conversations.ai')}</span>;
      case 'ai_suggested':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">⚡ {t('conversations.aiSuggested')}</span>;
      case 'human':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">👤 {t('conversations.human')}</span>;
      case 'hybrid':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">💬 {t('conversations.hybrid')}</span>;
      default:
        return null;
    }
  };

  const handleConversationClick = (conversation: any) => {
    setSelectedConversation(conversation);
    setMobileConversationView(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">{t('conversations.loading')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-3">{t('nav.conversations')}</h2>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('conversations.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 bg-gray-100 border-0 rounded-full focus:ring-2 focus:ring-gray-900 focus:bg-white text-sm"
          />
        </div>

        {/* Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 hover:bg-gray-100 rounded-full transition"
        >
          <Filter className="w-4 h-4" />
          <span className="font-medium">{t('common.filters')}</span>
          {(statusFilter || handlingModeFilter) && (
            <span className="px-2 py-0.5 text-xs bg-black text-white rounded-full">{t('common.active')}</span>
          )}
        </button>

        {/* Filters */}
        {showFilters && (
          <div className="mt-3 space-y-2">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5 px-1">{t('common.status')}</label>
              <select
                value={statusFilter || ''}
                onChange={(e) => setStatusFilter(e.target.value || null)}
                className="w-full px-4 py-2.5 bg-gray-100 border-0 rounded-full text-sm focus:ring-2 focus:ring-gray-900 focus:bg-white"
              >
                <option value="">{t('conversations.allStatuses')}</option>
                <option value="open">{t('common.open')}</option>
                <option value="pending">{t('common.pending')}</option>
                <option value="closed">{t('common.closed')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5 px-1">{t('conversations.handlingMode')}</label>
              <select
                value={handlingModeFilter || ''}
                onChange={(e) => setHandlingModeFilter(e.target.value || null)}
                className="w-full px-4 py-2.5 bg-gray-100 border-0 rounded-full text-sm focus:ring-2 focus:ring-gray-900 focus:bg-white"
              >
                <option value="">{t('conversations.allModes')}</option>
                <option value="ai">🤖 {t('conversations.aiHandling')}</option>
                <option value="ai_suggested">⚡ {t('conversations.aiSuggested')}</option>
                <option value="human">👤 {t('conversations.human')}</option>
                <option value="hybrid">💬 {t('conversations.hybrid')}</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{t('conversations.noConversations')}</p>
          </div>
        ) : (
          conversations.map((conversation) => (
            <button
              key={conversation.id}
              onClick={() => handleConversationClick(conversation)}
              className={`w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition text-left ${
                selectedConversation?.id === conversation.id ? 'bg-gray-100 border-l-4 border-l-black' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {getHandlingModeIcon(conversation.handling_mode)}
                  <span className="font-semibold text-gray-900 truncate">
                    {getDisplayName(conversation)}
                  </span>
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                  {formatDistanceToNow(new Date(conversation.updated_at), { addSuffix: true, locale: getDateLocale() })}
                </span>
              </div>

              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {getHandlingModeBadge(conversation.handling_mode)}
                {conversation.escalated_from_ai && (
                  <span className="px-2.5 py-1 text-xs rounded-full bg-orange-100 text-orange-800 font-medium">{t('conversations.escalated')}</span>
                )}
                <span className={`px-2.5 py-1 text-xs rounded-full font-medium ${
                  conversation.status === 'open' ? 'bg-green-100 text-green-800' :
                  conversation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-200 text-gray-800'
                }`}>
                  {conversation.status}
                </span>
              </div>

              {conversation.last_message && (
                <p className="text-sm text-gray-600 truncate">
                  {conversation.last_message.content}
                </p>
              )}

              {conversation.unread_count && conversation.unread_count > 0 && (
                <div className="mt-2">
                  <span className="px-2.5 py-1 text-xs bg-black text-white rounded-full font-medium">
                    {conversation.unread_count} {t('conversations.new')}
                  </span>
                </div>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
